import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { categoriesAPI, listingsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format, addDays } from 'date-fns';
import { ro } from 'date-fns/locale';
import { 
    Gavel, 
    Tag, 
    CalendarIcon, 
    Loader2,
    Info
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import ImageUpload from '@/components/upload/ImageUpload';

// Helper to set Romanian validation message for number inputs
const handleNumberInputInvalid = (e) => {
    e.target.setCustomValidity('Introduceți un număr');
};
const handleNumberInputInput = (e) => {
    e.target.setCustomValidity('');
};

const CreateListingPage = () => {
    const { isAuthenticated, user } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);
    
    // Get default listing type from URL parameter (?type=auction or ?type=sale)
    const defaultType = searchParams.get('type') === 'auction' ? 'auction' : 
                        searchParams.get('type') === 'sale' ? 'direct_sale' : 'direct_sale';
    
    const [datePickerOpen, setDatePickerOpen] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        category_id: '',
        subcategory_id: '',
        listing_type: defaultType,
        starting_price: '',
        buy_now_price: '',
        minimum_bid_increment: '1',
        end_date: addDays(new Date(), 7),
        end_time: '20:00',  // Default end time
        auction_type: 'fixed',
        condition: 'good',
        shipping_cost: '',
        return_policy: 'no_returns',
        images: [],
        accepts_trades: false,
    });

    useEffect(() => {
        if (!isAuthenticated) {
            toast.error('Trebuie să fii autentificat pentru a lista un produs');
            navigate('/login');
            return;
        }

        const fetchCategories = async () => {
            try {
                const res = await categoriesAPI.getAll();
                setCategories(res.data);
            } catch (error) {
                console.error('Error fetching categories:', error);
            }
        };

        fetchCategories();
    }, [isAuthenticated, navigate]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        
        if (field === 'category_id') {
            const cat = categories.find(c => c.category_id === value);
            setSelectedCategory(cat);
            setFormData(prev => ({ ...prev, subcategory_id: '' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.title.trim()) {
            toast.error('Titlul este obligatoriu');
            return;
        }
        if (!formData.category_id) {
            toast.error('Selectează o categorie');
            return;
        }
        if (!formData.subcategory_id) {
            toast.error('Selectează o subcategorie');
            return;
        }
        if (!formData.starting_price || parseFloat(formData.starting_price) <= 0) {
            toast.error('Prețul de pornire trebuie să fie mai mare decât 0');
            return;
        }

        setLoading(true);
        try {
            const payload = {
                ...formData,
                starting_price: parseFloat(formData.starting_price),
                buy_now_price: formData.buy_now_price ? parseFloat(formData.buy_now_price) : null,
                minimum_bid_increment: parseFloat(formData.minimum_bid_increment),
                shipping_cost: formData.shipping_cost === '' ? 0 : parseFloat(formData.shipping_cost),
                end_date: formData.listing_type === 'auction' ? (() => {
                    // Combine date and time
                    const date = new Date(formData.end_date);
                    const [hours, minutes] = formData.end_time.split(':').map(Number);
                    date.setHours(hours, minutes, 0, 0);
                    return date.toISOString();
                })() : null,
            };

            const res = await listingsAPI.create(payload);
            toast.success('Listare creată cu succes!');
            navigate(`/listing/${res.data.slug || res.data.listing_id}`);
        } catch (error) {
            const detail = error.response?.data?.detail;
            let errorMessage = 'Eroare la crearea listării';
            
            if (typeof detail === 'string') {
                errorMessage = detail;
            } else if (Array.isArray(detail)) {
                // Pydantic validation errors
                errorMessage = detail.map(e => e.msg || e.message || String(e)).join(', ');
            } else if (detail && typeof detail === 'object') {
                errorMessage = detail.msg || detail.message || JSON.stringify(detail);
            }
            
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background py-8" data-testid="create-listing-page">
            <div className="container mx-auto px-4 lg:px-8 max-w-3xl">
                <div className="mb-8">
                    <h1 className="font-heading text-3xl font-bold">Listează un produs</h1>
                    <p className="text-muted-foreground mt-2">
                        Completează detaliile pentru a lista produsul tău
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Listing Type */}
                    <Card className="rounded-2xl">
                        <CardHeader>
                            <CardTitle className="font-heading text-lg">Tip listare</CardTitle>
                            <CardDescription>Alege cum vrei să vinzi produsul</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-2 gap-4">
                                <button
                                    type="button"
                                    onClick={() => handleChange('listing_type', 'direct_sale')}
                                    className={cn(
                                        "p-4 rounded-xl border-2 text-left transition-all",
                                        formData.listing_type === 'direct_sale'
                                            ? "border-primary bg-blue-50"
                                            : "border-border hover:border-gray-300"
                                    )}
                                    data-testid="type-direct-sale"
                                >
                                    <Tag className={cn(
                                        "w-6 h-6 mb-2",
                                        formData.listing_type === 'direct_sale' ? "text-primary" : "text-muted-foreground"
                                    )} />
                                    <p className="font-semibold">Preț fix</p>
                                    <p className="text-sm text-muted-foreground">Preț fix, cumpărare imediată</p>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleChange('listing_type', 'auction')}
                                    className={cn(
                                        "p-4 rounded-xl border-2 text-left transition-all",
                                        formData.listing_type === 'auction'
                                            ? "border-primary bg-blue-50"
                                            : "border-border hover:border-gray-300"
                                    )}
                                    data-testid="type-auction"
                                >
                                    <Gavel className={cn(
                                        "w-6 h-6 mb-2",
                                        formData.listing_type === 'auction' ? "text-primary" : "text-muted-foreground"
                                    )} />
                                    <p className="font-semibold">Licitație</p>
                                    <p className="text-sm text-muted-foreground">Utilizatorii licitează pentru produs</p>
                                </button>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Basic Info */}
                    <Card className="rounded-2xl">
                        <CardHeader>
                            <CardTitle className="font-heading text-lg">Informații produs</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="title">Titlu *</Label>
                                <Input
                                    id="title"
                                    value={formData.title}
                                    onChange={(e) => handleChange('title', e.target.value)}
                                    placeholder="Ex: Medalie Meritul Cultural clasa I"
                                    className="rounded-xl"
                                    required
                                    data-testid="listing-title-input"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="description">Descriere</Label>
                                <Textarea
                                    id="description"
                                    value={formData.description}
                                    onChange={(e) => handleChange('description', e.target.value)}
                                    placeholder="Descriere detaliată a produsului..."
                                    rows={5}
                                    className="rounded-xl"
                                    data-testid="listing-description-input"
                                />
                            </div>

                            <div className="grid sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Categorie *</Label>
                                    <Select
                                        value={formData.category_id}
                                        onValueChange={(value) => handleChange('category_id', value)}
                                    >
                                        <SelectTrigger className="rounded-xl border-0 bg-muted/50 hover:bg-muted" data-testid="category-select">
                                            <SelectValue placeholder="Selectează categoria" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {categories.map((cat) => (
                                                <SelectItem key={cat.category_id} value={cat.category_id}>
                                                    {cat.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                {selectedCategory?.subcategories?.length > 0 && (
                                    <div className="space-y-2">
                                        <Label>Subcategorie</Label>
                                        <Select
                                            value={formData.subcategory_id}
                                            onValueChange={(value) => handleChange('subcategory_id', value)}
                                        >
                                            <SelectTrigger className="rounded-xl border-0 bg-muted/50 hover:bg-muted">
                                                <SelectValue placeholder="Selectează subcategoria" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {selectedCategory.subcategories.map((sub) => (
                                                    <SelectItem key={sub.subcategory_id} value={sub.subcategory_id}>
                                                        {sub.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}
                            </div>

                            <div className="grid sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Stare *</Label>
                                    <Select
                                        value={formData.condition}
                                        onValueChange={(value) => handleChange('condition', value)}
                                    >
                                        <SelectTrigger className="rounded-xl border-0 bg-muted/50 hover:bg-muted">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="new">Nou</SelectItem>
                                            <SelectItem value="like_new">Ca nou</SelectItem>
                                            <SelectItem value="good">Bună</SelectItem>
                                            <SelectItem value="fair">Acceptabilă</SelectItem>
                                            <SelectItem value="poor">Slabă</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Images */}
                    <Card className="rounded-2xl">
                        <CardHeader>
                            <CardTitle className="font-heading text-lg">Imagini</CardTitle>
                            <CardDescription>Încarcă imagini pentru produsul tău</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <ImageUpload 
                                images={formData.images} 
                                setImages={(newImages) => {
                                    if (typeof newImages === 'function') {
                                        setFormData(prev => ({ ...prev, images: newImages(prev.images) }));
                                    } else {
                                        setFormData(prev => ({ ...prev, images: newImages }));
                                    }
                                }}
                                maxImages={15}
                            />
                        </CardContent>
                    </Card>

                    {/* Pricing */}
                    <Card className="rounded-2xl">
                        <CardHeader>
                            <CardTitle className="font-heading text-lg">Preț</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="starting_price">
                                        {formData.listing_type === 'auction' ? 'Preț de pornire *' : 'Preț *'}
                                    </Label>
                                    <div className="relative">
                                        <Input
                                            id="starting_price"
                                            type="number"
                                            value={formData.starting_price}
                                            onChange={(e) => handleChange('starting_price', e.target.value)}
                                            onInvalid={handleNumberInputInvalid}
                                            onInput={handleNumberInputInput}
                                            placeholder="0"
                                            min="0"
                                            step="0.01"
                                            className="rounded-xl pr-12"
                                            required
                                            data-testid="listing-price-input"
                                        />
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">RON</span>
                                    </div>
                                </div>

                                {formData.listing_type === 'auction' && (
                                    <div className="space-y-2">
                                        <Label htmlFor="buy_now_price">Preț cumpără acum (opțional)</Label>
                                        <div className="relative">
                                            <Input
                                                id="buy_now_price"
                                                type="number"
                                                value={formData.buy_now_price}
                                                onChange={(e) => handleChange('buy_now_price', e.target.value)}
                                                onInvalid={handleNumberInputInvalid}
                                                onInput={handleNumberInputInput}
                                                placeholder="0"
                                                min="0"
                                                step="0.01"
                                                className="rounded-xl pr-12"
                                            />
                                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">RON</span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {formData.listing_type === 'auction' && (
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="minimum_bid_increment">Increment minim licitație</Label>
                                        <div className="relative">
                                            <Input
                                                id="minimum_bid_increment"
                                                type="number"
                                                value={formData.minimum_bid_increment}
                                                onChange={(e) => handleChange('minimum_bid_increment', e.target.value)}
                                                onInvalid={handleNumberInputInvalid}
                                                onInput={handleNumberInputInput}
                                                min="1"
                                                step="1"
                                                className="rounded-xl pr-12"
                                            />
                                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">RON</span>
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <Label>Tip licitație</Label>
                                        <Select
                                            value={formData.auction_type}
                                            onValueChange={(value) => handleChange('auction_type', value)}
                                        >
                                            <SelectTrigger className="rounded-xl border-0 bg-muted/50 hover:bg-muted">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="fixed">Termen fix</SelectItem>
                                                <SelectItem value="variable">Termen variabil (+5 min la ultimul bid)</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            )}

                            <div className="grid sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="shipping_cost">Cost transport</Label>
                                    <div className="relative">
                                        <Input
                                            id="shipping_cost"
                                            type="number"
                                            value={formData.shipping_cost}
                                            onChange={(e) => handleChange('shipping_cost', e.target.value)}
                                            placeholder="0"
                                            min="0"
                                            step="0.01"
                                            className="rounded-xl pr-12"
                                        />
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">RON</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground">Setează 0 pentru transport gratuit</p>
                                </div>

                                <div className="space-y-2">
                                    <Label>Politica retur</Label>
                                    <Select
                                        value={formData.return_policy}
                                        onValueChange={(value) => handleChange('return_policy', value)}
                                    >
                                        <SelectTrigger className="rounded-xl border-0 bg-muted/50 hover:bg-muted">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="no_returns">Nu acceptă retururi</SelectItem>
                                            <SelectItem value="7_days">Retur în 7 zile</SelectItem>
                                            <SelectItem value="14_days">Retur în 14 zile</SelectItem>
                                            <SelectItem value="30_days">Retur în 30 zile</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                
                                <div className="flex items-center space-x-2 pt-2">
                                    <input
                                        type="checkbox"
                                        id="accepts_trades"
                                        checked={formData.accepts_trades}
                                        onChange={(e) => handleChange('accepts_trades', e.target.checked)}
                                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                    />
                                    <Label htmlFor="accepts_trades" className="cursor-pointer">Accept schimburi</Label>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Auction End Date */}
                    {formData.listing_type === 'auction' && (
                        <Card className="rounded-2xl">
                            <CardHeader>
                                <CardTitle className="font-heading text-lg">Data și ora expirării licitației</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    {/* Date Picker */}
                                    <div className="space-y-2">
                                        <Label>Data</Label>
                                        <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                                            <PopoverTrigger asChild>
                                                <Button variant="outline" className="w-full justify-start rounded-xl">
                                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                                    {format(formData.end_date, 'PPP', { locale: ro })}
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-auto p-0" align="start">
                                                <Calendar
                                                    mode="single"
                                                    selected={formData.end_date}
                                                    onSelect={(date) => {
                                                        if (date) {
                                                            handleChange('end_date', date);
                                                            setDatePickerOpen(false);
                                                        }
                                                    }}
                                                    disabled={(date) => date < new Date()}
                                                    initialFocus
                                                />
                                            </PopoverContent>
                                        </Popover>
                                    </div>
                                    
                                    {/* Time Picker with Dropdowns */}
                                    <div className="space-y-2">
                                        <Label>Ora expirării</Label>
                                        <div className="flex gap-2">
                                            <Select
                                                value={formData.end_time.split(':')[0]}
                                                onValueChange={(hour) => {
                                                    const minutes = formData.end_time.split(':')[1] || '00';
                                                    handleChange('end_time', `${hour}:${minutes}`);
                                                }}
                                            >
                                                <SelectTrigger className="w-24 rounded-xl border-0 bg-muted/50 hover:bg-muted">
                                                    <SelectValue placeholder="Ora" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0')).map(hour => (
                                                        <SelectItem key={hour} value={hour}>{hour}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            <span className="flex items-center text-lg font-bold">:</span>
                                            <Select
                                                value={formData.end_time.split(':')[1] || '00'}
                                                onValueChange={(minute) => {
                                                    const hour = formData.end_time.split(':')[0] || '20';
                                                    handleChange('end_time', `${hour}:${minute}`);
                                                }}
                                            >
                                                <SelectTrigger className="w-24 rounded-xl border-0 bg-muted/50 hover:bg-muted">
                                                    <SelectValue placeholder="Min" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {['00', '15', '30', '45'].map(minute => (
                                                        <SelectItem key={minute} value={minute}>{minute}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                </div>
                                <p className="text-sm text-muted-foreground">
                                    Licitația va expira pe {format(formData.end_date, 'PPP', { locale: ro })} la ora {formData.end_time}
                                </p>
                            </CardContent>
                        </Card>
                    )}

                    {/* Platform Fee Info - TEMPORARILY HIDDEN
                    <div className="flex items-start gap-3 p-4 rounded-xl bg-blue-50 text-sm">
                        <Info className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                        <div>
                            <p className="font-medium text-foreground">Comision platformă: 10%</p>
                            <p className="text-muted-foreground">
                                La vânzarea produsului, se va reține un comision de 10% din prețul final. 
                                Banii sunt ținuți în siguranță până la confirmarea livrării.
                            </p>
                        </div>
                    </div>
                    */}

                    {/* Submit */}
                    <Button
                        type="submit"
                        className="w-full rounded-full bg-primary hover:bg-primary/90 h-12 text-lg"
                        disabled={loading}
                        data-testid="submit-listing"
                    >
                        {loading ? (
                            <Loader2 className="w-5 h-5 animate-spin mr-2" />
                        ) : formData.listing_type === 'auction' ? (
                            <Gavel className="w-5 h-5 mr-2" />
                        ) : (
                            <Tag className="w-5 h-5 mr-2" />
                        )}
                        {formData.listing_type === 'auction' ? 'Publică licitația' : 'Publică vânzarea'}
                    </Button>
                </form>
            </div>
        </div>
    );
};

export default CreateListingPage;
